import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import type { WorkLog, WorkRecord } from '../../types';
import StateBlock from '../ui/StateBlock';
import { formatDateTime } from '../../utils/format';
import InlineTimeRange from './InlineTimeRange';
import { formatDuration, WorkLogSummary } from './workLogsTable';

interface Props {
  record: WorkRecord;
  /** This cycle's segments (all logs, grouped by `work_record_id`). */
  segments: WorkLog[];
  /** D-292 - the segment's start / end time is edited in place. */
  canEditTimes?: boolean;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

/**
 * The detail of one work cycle, shown in its expanded table row (DataTable
 * `renderExpanded`; was a dialog): the summary and the time segments. A
 * cycle has a handful of segments, so it stays inline; a long list scrolls.
 */
export default function WorkRecordSegments({ record, segments, canEditTimes = false, loading, error, onRetry }: Props) {
  const { t } = useTranslation();
  const isOpen = record.status === 'open';
  const headingId = `wr-seg-${record.id}`;

  return (
    <div className="max-w-3xl space-y-2.5">
      <p className={`text-sm ${record.summary ? 'text-text' : isOpen ? 'text-warning-700' : 'text-text-2'}`}>
        {record.summary || (isOpen ? t('records.noSummaryOpen') : t('records.noSummary'))}
      </p>
      <section aria-labelledby={headingId}>
        <h3 id={headingId} className="mb-1.5 text-xs font-medium text-text-2">{t('workLogs.cycles.segmentsTitle')}</h3>
        {loading ? (
          <StateBlock kind="loading" size="inline" />
        ) : error ? (
          <StateBlock kind="error" size="inline" title={t('workLogs.states.segmentsError')} description={null} onRetry={onRetry} />
        ) : segments.length === 0 ? (
          <p className="text-sm text-text-2">{t('records.noSegments')}</p>
        ) : (
          <ul className="max-h-64 divide-y divide-border overflow-y-auto rounded-lg border border-border bg-surface">
            {segments.map((seg) => (
              <li key={seg.id} className="flex flex-wrap items-center gap-x-4 gap-y-0.5 px-3 py-2 text-sm">
                <p className="min-w-0 basis-full text-text sm:basis-0 sm:flex-1">
                  <WorkLogSummary summary={seg.summary} ended={!!seg.end_time} t={t} className="" empty={<bdi>{t('records.segmentNoSummary')}</bdi>} />
                </p>
                <span className="whitespace-nowrap text-xs text-text-2 tabular-nums" dir="ltr">
                  {canEditTimes && seg.start_time
                    ? <InlineTimeRange log={seg} canEdit />
                    : <>{formatDateTime(seg.start_time)}{seg.end_time ? `–${format(new Date(seg.end_time), 'HH:mm')}` : ''}</>}
                </span>
                <span className="whitespace-nowrap font-medium text-text tabular-nums">{formatDuration(seg.duration_minutes ?? 0, t)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
